import React, { useEffect, useMemo, useState } from "react";
import api, { withCeoPassword } from "@/lib/api";
import { Plus, Trash2, Send, Eye, Check, X, FileText, Edit, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import SearchableSelect from "@/components/SearchableSelect";
import TableExportButton from "@/components/TableExportButton";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const todayISO = () => new Date().toISOString().slice(0, 10);

const UNIT_OPTIONS = [
  { value: "Kg", label: "Kg" },
  { value: "Liters", label: "Liters" },
  { value: "Nos", label: "Nos" },
];

const STATUS_LABEL = {
  pending: "Pending L1",
  pending_l2: "Pending L2",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  pending_l2: "bg-[#1D2951]/10 text-[#1D2951]",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

function emptyItem() {
  return {
    description: "",
    item_id: "",
    item_code: "",
    category: "",
    make: "",
    quantity: 1,
    approx_cost_per_unit: 0,
    tax_rate: 0,
    unit: "Nos",
    hsn_sac: "",
  };
}

export default function IndentSheet() {
  const { user } = useAuth();
  const [intenders, setIntenders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [plants, setPlants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [requiredForOptions, setRequiredForOptions] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  // Category → Cost Center mapping (Feb-2026). Powers bidirectional auto-fill.
  const [categoryToCC, setCategoryToCC] = useState({});
  const [indents, setIndents] = useState([]);
  const [view, setView] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwdMode, setPwdMode] = useState(null); // "approve" | "reject"
  const navigate = useNavigate();

  const [form, setForm] = useState({
    date: todayISO(),
    plant: "",
    plant_address: "",
    company_name: "",
    company_address: "",
    company_gst: "",
    department: "",
    required_for: "",
    cost_center: "",
    intender_name: "",
    supplier_name: "",
    items: [emptyItem()],
    purchase_type: "cash",
    credit_days: 0,
    purchase_group: "",
    process: "",
    remarks: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [listQ, setListQ] = useState("");
  // Bulk approval state — checked indent ids + the password dialog state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialog, setBulkDialog] = useState(false);  // open when user clicks "Approve Selected"
  const [bulkPwd, setBulkPwd] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const filteredIndents = useMemo(() => {
    const s = listQ.trim().toLowerCase();
    if (!s) return indents;
    return indents.filter((i) =>
      [
        i.indent_no, i.date, i.plant, i.department, i.required_for, i.cost_center,
        i.intender_name, i.supplier_name, i.purchase_group, i.status,
        STATUS_LABEL[i.status],
        ...(i.items || []).flatMap((it) => [it.description, it.item_code, it.category, it.make]),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [indents, listQ]);

  const resetForm = () => {
    setForm({
      date: todayISO(),
      plant: "",
      plant_address: "",
      company_name: "",
      company_address: "",
      company_gst: "",
      department: "",
      required_for: "",
      cost_center: "",
      intender_name: "",
      supplier_name: "",
      items: [emptyItem()],
      purchase_type: "cash",
      credit_days: 0,
      purchase_group: "",
      process: "",
      remarks: "",
    });
    setEditingId(null);
  };

  const startEdit = (indent) => {
    setEditingId(indent.id);
    setForm({
      date: indent.date || todayISO(),
      plant: indent.plant || "",
      plant_address: indent.plant_address || "",
      company_name: indent.company_name || "",
      company_address: indent.company_address || "",
      company_gst: indent.company_gst || "",
      department: indent.department || "",
      required_for: indent.required_for || "",
      cost_center: indent.cost_center || "",
      intender_name: indent.intender_name || "",
      supplier_name: indent.supplier_name || "",
      purchase_group: indent.purchase_group || "",
      process: indent.process || "",
      items: (indent.items || []).map((it) => ({
        description: it.description || "",
        item_id: it.item_id || "",
        item_code: it.item_code || "",
        category: it.category || "",
        make: it.make || "",
        quantity: Number(it.quantity || 0),
        approx_cost_per_unit: Number(it.approx_cost_per_unit ?? it.approx_cost ?? 0),
        tax_rate: Number(it.tax_rate || 0),
        unit: it.unit || "Nos",
        hsn_sac: it.hsn_sac || "",
      })),
      purchase_type: indent.purchase_type || "cash",
      credit_days: Number(indent.credit_days || 0),
      remarks: indent.remarks || "",
    });
    setView(null);
    // Scroll to the form
    setTimeout(() => {
      document.querySelector('[data-testid="indent-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const loadAll = async () => {
    const [i, s, it, st, ind, cc, deps, reqs, procs, comps, cats] = await Promise.all([
      api.get("/intenders"),
      api.get("/suppliers"),
      api.get("/items"),
      api.get("/settings"),
      api.get("/indents"),
      api.get("/cost-centers"),
      api.get("/departments"),
      api.get("/requireds"),
      api.get("/processes"),
      api.get("/companies-book").catch(() => ({ data: [] })),
      api.get("/categories").catch(() => ({ data: [] })),
    ]);
    setIntenders(i.data);
    setSuppliers(s.data);
    setItems(it.data);
    // Department options: Master collection takes priority, fall back to settings
    const masterDeps = (deps.data || []).map((d) => d.name);
    setDepartments(masterDeps.length ? masterDeps : (st.data.departments || []));
    setPlants(st.data.plants || []);
    setCompanies(comps.data || []);
    // Required-For options: Master collection takes priority, fall back to settings
    const masterReqs = (reqs.data || []).map((r) => r.name);
    setRequiredForOptions(masterReqs.length ? masterReqs : (st.data.required_for_options || []));
    setIndents(ind.data);
    setCostCenters(cc.data);
    setProcesses((procs.data || []).map((p) => p.name));
    // Build the Category → Cost Center lookup (case-insensitive)
    const map = {};
    for (const c of (cats.data || [])) {
      const cname = (c.name || "").trim().toLowerCase();
      const cc = (c.cost_center || "").trim();
      if (cname && cc) map[cname] = cc;
    }
    setCategoryToCC(map);
  };

  useEffect(() => { loadAll(); }, []);

  const subTotal = useMemo(
    () =>
      form.items.reduce(
        (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.approx_cost_per_unit) || 0),
        0
      ),
    [form.items]
  );
  const taxAmount = useMemo(
    () =>
      form.items.reduce(
        (sum, it) => {
          const lv = (Number(it.quantity) || 0) * (Number(it.approx_cost_per_unit) || 0);
          return sum + lv * (Number(it.tax_rate) || 0) / 100;
        },
        0
      ),
    [form.items]
  );
  const totalIndentValue = subTotal + taxAmount;
  const requiredLevel = totalIndentValue <= 1000 ? 1 : 2;

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const its = [...f.items];
      its[idx] = { ...its[idx], ...patch };
      return { ...f, items: its };
    });
  };

  const onPickItem = async (idx, itemId) => {
    const item = items.find((x) => x.id === itemId);
    if (!item) return;
    updateItem(idx, {
      item_id: item.id,
      item_code: item.item_code,
      description: item.description,
      category: item.category,
      make: item.make || "",
      purchase_group: item.purchase_group || "",
      unit: item.unit || "Nos",
      hsn_sac: item.hsn_code || "",
      grade: item.grade || "",
      rm_type: item.rm_type || "",
    });
    // Auto-apply Purchase Group at indent level if not set yet
    if (item.purchase_group && !form.purchase_group) {
      setForm((f) => ({ ...f, purchase_group: item.purchase_group }));
    }
    // Category ↔ Cost Center bidirectional auto-fill (Feb-2026).
    //   Case 1: Item's category maps to a Cost Center → fill `form.cost_center`
    //           when it's blank so the user doesn't have to pick both.
    //   Case 2: When cost_center is already set, we leave it alone.
    const catKey = (item.category || "").trim().toLowerCase();
    if (catKey && categoryToCC[catKey] && !form.cost_center) {
      setForm((f) => ({ ...f, cost_center: categoryToCC[catKey] }));
    }
    // ---- Indent memory: prefill header from the last indent that used this item.
    //      Only fills CURRENTLY-EMPTY fields, so it never clobbers anything the
    //      user already typed. Quantity & cost are intentionally NOT included.
    try {
      const { data: hist } = await api.get(`/indents/last-used/${item.id}`);
      if (hist?.found) {
        setForm((f) => {
          const next = { ...f };
          const setIfBlank = (k, v) => { if (!next[k] && v) next[k] = v; };
          setIfBlank("plant", hist.plant);
          setIfBlank("plant_address", hist.plant_address);
          setIfBlank("company_name", hist.company_name);
          setIfBlank("company_address", hist.company_address);
          setIfBlank("company_gst", hist.company_gst);
          setIfBlank("department", hist.department);
          setIfBlank("required_for", hist.required_for);
          setIfBlank("cost_center", hist.cost_center);
          setIfBlank("purchase_group", hist.purchase_group);
          setIfBlank("process", hist.process);
          setIfBlank("intender_name", hist.intender_name);
          setIfBlank("supplier_id", hist.supplier_id);
          setIfBlank("supplier_name", hist.supplier_name);
          return next;
        });
        // Also apply per-item carry-forwards (tax_rate / hsn / make / spec) if blank
        const d = hist.item_defaults || {};
        updateItem(idx, {
          ...(item.tax_rate ? {} : (d.tax_rate ? { tax_rate: d.tax_rate } : {})),
          ...(item.hsn_code ? {} : (d.hsn_sac ? { hsn_sac: d.hsn_sac } : {})),
          ...(item.make ? {} : (d.make ? { make: d.make } : {})),
          ...(d.item_specification ? { item_specification: d.item_specification } : {}),
        });
        toast.message(`Auto-filled from last indent on ${hist.source_indent_date}`, { duration: 2500 });
      }
    } catch {
      // Silent — memory is best-effort, never block the form.
    }
  };

  const onPickPlant = (name) => {
    const p = plants.find((x) => x.name === name);
    setForm((f) => ({ ...f, plant: name, plant_address: p?.address || "" }));
  };

  const removeIndent = async (i) => {
    if (!window.confirm(`Delete indent submitted on ${i.date}?\n\nIf already approved or has a PO, you will be prompted for the CEO password.`)) return;
    try {
      await withCeoPassword((cfg) => api.delete(`/indents/${i.id}`, cfg));
      toast.success("Indent deleted");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.department || !form.intender_name || !form.supplier_name || !form.plant) {
      toast.error("Plant, Department, Intender and Supplier are required");
      return;
    }
    if (form.items.some((it) => !it.item_id || !it.description)) {
      toast.error("Each item must be selected from Item Master");
      return;
    }
    if (form.purchase_group === "labour_work" && !form.process) {
      toast.error("Please select a Process — required for Labour Work");
      return;
    }
    try {
      if (editingId) {
        await withCeoPassword((cfg) => api.put(`/indents/${editingId}`, form, cfg));
        toast.success("Indent updated");
      } else {
        await api.post("/indents", form);
        toast.success("Indent submitted for approval");
      }
      resetForm();
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    }
  };

  const askPwd = (mode) => { setPwd(""); setPwdMode(mode); };

  const submitDecision = async () => {
    if (!pwd) return toast.error("Password required");
    try {
      const url = `/indents/${view.id}/${pwdMode}`;
      await api.post(url, { password: pwd });
      toast.success(pwdMode === "approve" ? "Approved" : "Rejected");
      setPwdMode(null);
      setPwd("");
      setView(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const canApprove = (indent) => {
    if (!user) return false;
    if (indent.status === "approved" || indent.status === "rejected") return false;
    // L1 step: gm or admin
    if (indent.status === "pending") return user.role === "gm" || user.role === "admin";
    // L2 step: admin only
    if (indent.status === "pending_l2") return user.role === "admin";
    return false;
  };

  // Indents currently selected for bulk approval — keep in sync with the
  // filtered list so users don't accidentally approve rows they can't see.
  const selectableIndents = useMemo(
    () => filteredIndents.filter((i) => canApprove(i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredIndents, user]
  );
  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const all = selectableIndents.map((i) => i.id);
    setSelectedIds((prev) => prev.length === all.length ? [] : all);
  };
  const submitBulkApproval = async () => {
    if (!bulkPwd) return toast.error("Password required");
    if (selectedIds.length === 0) return toast.error("Nothing selected");
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/indents/bulk-approve", {
        indent_ids: selectedIds,
        password: bulkPwd,
      });
      const failed = (data.results || []).filter((r) => !r.ok);
      if (failed.length === 0) {
        toast.success(`Approved ${data.approved} indent${data.approved === 1 ? "" : "s"}`);
      } else {
        toast.warning(`Approved ${data.approved}/${data.total}. ${failed.length} failed — first: ${failed[0]?.error || "unknown"}`);
      }
      setBulkDialog(false);
      setBulkPwd("");
      setSelectedIds([]);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approval failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            Module · Procurement
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Indent Sheet
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Capture material requirements. ≤ ₹1000 = Level 1 approval; &gt; ₹1000 = Level 1 then Level 2 (CEO).
          </p>
        </div>
        <TableExportButton
          testIdPrefix="indent-export"
          filename="indent_log"
          rows={indents.flatMap((ind) =>
            (ind.items || []).map((it, idx) => ({
              indent_no: ind.indent_no,
              date: (ind.date || ind.created_at || "").slice(0, 10),
              plant: ind.plant || "",
              company: ind.company_name || "",
              department: ind.department || "",
              cost_center: ind.cost_center || "",
              required_for: ind.required_for || "",
              purchase_group: ind.purchase_group || "",
              supplier: ind.supplier || "",
              intender: ind.intender_name || "",
              status: ind.status || "",
              current_level: ind.current_level || "",
              required_level: ind.required_level || "",
              item_code: it.item_code || "",
              description: it.description || "",
              hsn_sac: it.hsn_sac || "",
              quantity: it.quantity ?? "",
              unit: it.unit || "",
              unit_price: it.unit_price ?? "",
              tax_rate: it.tax_rate ?? "",
              line_total: idx === 0 ? (ind.items || []).reduce((s, x) => s + Number(x.quantity || 0) * Number(x.unit_price || 0), 0) : "",
            }))
          )}
          dateField="date"
          columns={[
            { key: "indent_no", label: "Indent No" },
            { key: "date", label: "Date" },
            { key: "plant", label: "Plant" },
            { key: "company", label: "Company" },
            { key: "department", label: "Department" },
            { key: "cost_center", label: "Cost Centre" },
            { key: "required_for", label: "Required For" },
            { key: "purchase_group", label: "Purchase Group" },
            { key: "supplier", label: "Supplier" },
            { key: "intender", label: "Indentor" },
            { key: "status", label: "Status" },
            { key: "current_level", label: "Current Level" },
            { key: "required_level", label: "Required Level" },
            { key: "item_code", label: "Item Code" },
            { key: "description", label: "Description" },
            { key: "hsn_sac", label: "HSN/SAC" },
            { key: "quantity", label: "Qty" },
            { key: "unit", label: "Unit" },
            { key: "unit_price", label: "Unit Price" },
            { key: "tax_rate", label: "Tax %" },
            { key: "line_total", label: "Indent Total" },
          ]}
        />
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-lg overflow-hidden"
        data-testid="indent-form"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">
            {editingId ? "Edit Indent" : "New Indent"}
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} data-testid="indent-cancel-edit" className="text-xs text-slate-600 hover:underline inline-flex items-center gap-1">
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Date">
            <input type="date" data-testid="indent-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Plant">
            <SearchableSelect
              testid="indent-plant"
              options={plants.map((p) => ({ value: p.name, label: p.name, sub: p.address }))}
              value={form.plant}
              onChange={onPickPlant}
              placeholder="Choose plant"
            />
          </Field>
          <Field label="Plant Address (auto)">
            <input value={form.plant_address} readOnly className={`${inputCls} bg-slate-50 text-slate-600`} placeholder="—" />
          </Field>
          <Field label="Company">
            <SearchableSelect
              testid="indent-company"
              options={companies.map((c) => ({ value: c.name, label: c.name, sub: c.gst || c.address }))}
              value={form.company_name}
              onChange={(v) => {
                const c = companies.find((x) => x.name === v) || {};
                setForm({ ...form, company_name: v, company_address: c.address || "", company_gst: c.gst || "" });
              }}
              placeholder="Choose company (optional)"
            />
          </Field>
          <Field label="Department">
            <SearchableSelect
              testid="indent-department"
              options={departments.map((d) => ({ value: d, label: d }))}
              value={form.department}
              onChange={(v) => setForm({ ...form, department: v })}
              placeholder="Choose department"
            />
          </Field>
          <Field label="Required For">
            <SearchableSelect
              testid="indent-required-for"
              options={requiredForOptions.map((d) => ({ value: d, label: d }))}
              value={form.required_for}
              onChange={(v) => setForm({ ...form, required_for: v })}
              placeholder="Machine Shop, Welding Shop, …"
              allowCustom
            />
          </Field>
          <Field label="Cost Center">
            <SearchableSelect
              testid="indent-cost-center"
              options={costCenters.map((c) => ({ value: c.name, label: c.name }))}
              value={form.cost_center}
              onChange={(v) => setForm({ ...form, cost_center: v })}
              placeholder="Choose cost center"
              allowCustom
            />
          </Field>
          <Field label="Purchase Group">
            <SearchableSelect
              testid="indent-purchase-group"
              options={[
                { value: "direct_purchase", label: "Direct Purchase" },
                { value: "indirect_purchase", label: "Indirect Purchase" },
                { value: "labour_work", label: "Labour Work" },
              ]}
              value={form.purchase_group}
              onChange={(v) => setForm({ ...form, purchase_group: v, process: v === "labour_work" ? form.process : "" })}
              placeholder="Direct / Indirect / Labour"
            />
          </Field>
          {form.purchase_group === "labour_work" && (
            <Field label="Process" required>
              <SearchableSelect
                testid="indent-process"
                options={processes.map((p) => ({ value: p, label: p }))}
                value={form.process}
                onChange={(v) => setForm({ ...form, process: v })}
                placeholder="Select process (add via Process Master)"
              />
            </Field>
          )}
          <Field label="Intender Name">
            <SearchableSelect
              testid="indent-intender"
              options={intenders.map((i) => ({ value: i.name, label: i.name }))}
              value={form.intender_name}
              onChange={(v) => setForm({ ...form, intender_name: v })}
              placeholder="Choose intender"
            />
          </Field>
          <Field label="Supplier Name">
            <SearchableSelect
              testid="indent-supplier"
              options={suppliers.map((s) => ({ value: s.supplier_name, label: s.supplier_name, sub: s.supplier_code }))}
              value={form.supplier_name}
              onChange={(v) => setForm({ ...form, supplier_name: v })}
              placeholder="Choose supplier"
            />
          </Field>
          <Field label="Purchase Type">
            <select data-testid="indent-purchase-type" value={form.purchase_type} onChange={(e) => setForm({ ...form, purchase_type: e.target.value })} className={inputCls}>
              <option value="cash">Cash Purchase</option>
              <option value="credit">Credit Purchase</option>
            </select>
          </Field>
          {form.purchase_type === "credit" && (
            <Field label="Credit Days">
              <input type="number" min="0" data-testid="indent-credit-days" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: Number(e.target.value) })} className={inputCls} />
            </Field>
          )}
        </div>

        <div className="px-6 pb-2 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Items</div>
            <div className="text-xs text-slate-500">Item not in master? <button type="button" className="text-[#1D2951] hover:underline font-semibold" onClick={() => navigate("/masters/item")}>Open Item Master →</button></div>
          </div>
          <button type="button" data-testid="indent-add-item" onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))} className="text-xs font-semibold text-[#1D2951] hover:underline inline-flex items-center gap-1">
            <Plus size={14} /> Add Row
          </button>
        </div>

        <div className="px-6 pb-6 space-y-3">
          {form.items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-3 p-3 border border-slate-200 rounded-md bg-slate-50/50" data-testid={`indent-item-${idx}`}>
              <div className="col-span-12 md:col-span-2">
                <Label>Item Description</Label>
                <SearchableSelect
                  testid={`item-pick-${idx}`}
                  options={items.map((m) => ({ value: m.id, label: m.description, sub: `${m.item_code} · ${m.category}${m.make ? " · " + m.make : ""}` }))}
                  value={it.item_id}
                  onChange={(v) => onPickItem(idx, v)}
                  placeholder=""
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <Label>Item Code</Label>
                <input className={`${inputCls} font-mono bg-slate-50 text-slate-600`} readOnly value={it.item_code} placeholder="—" />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Label>Category</Label>
                <input className={`${inputCls} bg-slate-50 text-slate-600`} readOnly value={it.category} placeholder="—" />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Label>Make</Label>
                <input data-testid={`item-make-${idx}`} className={`${inputCls} bg-slate-50 text-slate-600`} readOnly value={it.make || ""} placeholder="—" />
              </div>
              <div className="col-span-4 md:col-span-1">
                <Label>Qty</Label>
                <input type="number" min="0" step="0.01" className={inputCls} value={it.quantity} onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })} />
              </div>
              <div className="col-span-4 md:col-span-1">
                <Label>Unit</Label>
                <SearchableSelect
                  options={UNIT_OPTIONS}
                  value={it.unit}
                  onChange={(v) => updateItem(idx, { unit: v })}
                  allowCustom
                  placeholder="Unit"
                />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Label>Cost / Unit</Label>
                <input type="number" min="0" step="0.01" className={inputCls} value={it.approx_cost_per_unit} onChange={(e) => updateItem(idx, { approx_cost_per_unit: Number(e.target.value) })} />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Label>Tax %</Label>
                <input
                  type="number" min="0" step="0.01"
                  className={inputCls}
                  value={it.tax_rate ?? 0}
                  onChange={(e) => updateItem(idx, { tax_rate: Number(e.target.value) })}
                  data-testid={`item-tax-${idx}`}
                  placeholder="0"
                />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Label>Line Total</Label>
                <div className="border border-slate-200 bg-white rounded-md px-3 py-2 text-sm font-mono text-slate-700 truncate" title={`Value ₹${((it.quantity || 0) * (it.approx_cost_per_unit || 0)).toFixed(2)} + Tax ₹${(((it.quantity || 0) * (it.approx_cost_per_unit || 0)) * (Number(it.tax_rate) || 0) / 100).toFixed(2)}`}>
                  ₹{(((it.quantity || 0) * (it.approx_cost_per_unit || 0)) * (1 + (Number(it.tax_rate) || 0) / 100)).toFixed(2)}
                </div>
              </div>
              <div className="col-span-12 md:col-span-1 flex md:items-end justify-end">
                {form.items.length > 1 && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="p-2 text-rose-600 hover:bg-rose-50 rounded-md">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
            Remarks / Notes <span className="text-slate-400 font-normal normal-case">— shown on the Indent slip and printed on the PO (bottom-left)</span>
          </label>
          <textarea
            data-testid="indent-remarks"
            value={form.remarks || ""}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            placeholder="Any special instructions, delivery notes, or context for the buyer / approver…"
            rows={3}
            className={`${inputCls} resize-y min-h-[70px]`}
          />
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between bg-slate-50 gap-4">
          <div className="text-sm space-x-6">
            <span className="text-slate-500">Indent Value · </span>
            <span className="font-mono text-slate-900">₹{subTotal.toFixed(2)}</span>
            <span className="text-slate-500">+ Tax · </span>
            <span className="font-mono text-slate-900">₹{taxAmount.toFixed(2)}</span>
            <span className="text-slate-500">= Total · </span>
            <span className="font-mono font-semibold text-slate-900 text-base">₹{totalIndentValue.toFixed(2)}</span>
            <span className={`ml-3 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${requiredLevel === 1 ? "bg-emerald-100 text-emerald-700" : "bg-[#1D2951]/10 text-[#1D2951]"}`}>
              Level {requiredLevel} approval
            </span>
          </div>
          <button data-testid="indent-submit" type="submit" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
            <Send size={14} /> {editingId ? "Update Indent" : "Submit"}
          </button>
        </div>
      </form>

      {/* Pending Budget Approval panel — shows indents where the cost centre's
          spend has crossed 75% of the FY budget and the owner needs to sign off
          before the indent can proceed. Only visible when the panel actually
          has rows (keeps the sheet clean when everything's within budget). */}
      <PendingBudgetPanel />

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-3">
            {user?.role === "user" ? "My Indents" : "All Indents"}
            {selectedIds.length > 0 && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold" data-testid="bulk-selected-count">
                {selectedIds.length} selected
              </span>
            )}
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            {selectedIds.length > 0 && (
              <>
                <button
                  onClick={() => { setBulkPwd(""); setBulkDialog(true); }}
                  data-testid="bulk-approve-btn"
                  className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <Check size={12} /> Approve Selected ({selectedIds.length})
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  data-testid="bulk-clear-btn"
                  className="px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold"
                >
                  Clear
                </button>
              </>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="indent-list-search"
                value={listQ}
                onChange={(e) => setListQ(e.target.value)}
                placeholder="Search by indent no, date, plant, dept, supplier, item, status…"
                className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-96 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-center px-3 py-3 w-8">
                  {selectableIndents.length > 0 && (
                    <input
                      type="checkbox"
                      data-testid="bulk-select-all"
                      checked={selectedIds.length === selectableIndents.length && selectableIndents.length > 0}
                      onChange={toggleSelectAll}
                      className="cursor-pointer"
                      title="Select all approvable indents in this view"
                    />
                  )}
                </th>
                <th className="text-left px-6 py-3">Indent No.</th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">Plant</th>
                <th className="text-left px-6 py-3">Dept</th>
                <th className="text-left px-6 py-3">Intender</th>
                <th className="text-left px-6 py-3">Supplier</th>
                <th className="text-right px-6 py-3">Total</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="indents-list">
              {filteredIndents.length === 0 && (
                <tr><td colSpan="10" className="text-center py-12 text-slate-400">{indents.length === 0 ? "No indents yet" : "No matches for your search"}</td></tr>
              )}
              {filteredIndents.map((i) => {
                const approvable = canApprove(i);
                const checked = selectedIds.includes(i.id);
                return (
                <tr key={i.id} className={`border-t border-slate-100 hover:bg-slate-50 ${checked ? "bg-emerald-50/40" : ""}`} data-testid={`indent-row-${i.id}`}>
                  <td className="px-3 py-3 text-center">
                    {approvable && (
                      <input
                        type="checkbox"
                        data-testid={`bulk-select-${i.id}`}
                        checked={checked}
                        onChange={() => toggleSelect(i.id)}
                        className="cursor-pointer"
                      />
                    )}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs font-semibold text-[#1D2951]">{i.indent_no || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs">{i.date}</td>
                  <td className="px-6 py-3">{i.plant || "—"}</td>
                  <td className="px-6 py-3">{i.department}</td>
                  <td className="px-6 py-3">{i.intender_name || "—"}</td>
                  <td className="px-6 py-3">{i.supplier_name}</td>
                  <td className="px-6 py-3 text-right font-mono">₹{(i.total_indent_value ?? i.approx_value ?? 0).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${STATUS_BADGE[i.status] || "bg-slate-100 text-slate-700"}`}>
                      {STATUS_LABEL[i.status] || i.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex gap-2 justify-end items-center">
                      <button
                        onClick={() => startEdit(i)}
                        data-testid={`edit-indent-${i.id}`}
                        className="text-[#1D2951] hover:underline text-xs font-semibold inline-flex items-center gap-1"
                        title={(i.status === "pending" || i.status === "pending_l2") ? "Edit" : "Edit (CEO password required)"}
                      >
                        <Edit size={12} /> Edit
                      </button>
                      <button onClick={() => setView(i)} className="text-[#1D2951] hover:underline text-xs font-semibold inline-flex items-center gap-1" data-testid={`view-indent-${i.id}`}>
                        <Eye size={12} /> View
                      </button>
                      {user?.role === "admin" && (
                        <button
                          onClick={() => removeIndent(i)}
                          data-testid={`delete-indent-${i.id}`}
                          className="text-rose-600 hover:underline text-xs font-semibold inline-flex items-center gap-1"
                          title={i.status === "approved" ? "Delete (CEO password)" : "Delete"}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* View dialog */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Indent Preview</DialogTitle>
            <DialogDescription>Review details before approval. Approver password is required.</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <KV k="Date" v={view.date} />
                <KV k="Plant" v={view.plant} />
                <KV k="Department" v={view.department} />
                <KV k="Required For" v={view.required_for} />
                <KV k="Cost Center" v={view.cost_center} />
                <KV k="Intender" v={view.intender_name} />
                <KV k="Supplier" v={view.supplier_name} />
                <KV k="Purchase Type" v={view.purchase_type === "credit" ? `Credit (${view.credit_days}d)` : "Cash"} />
                <KV k="Status" v={STATUS_LABEL[view.status] || view.status} />
              </div>
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-left">Unit</th>
                    <th className="px-3 py-2 text-right">Cost/Unit</th>
                    <th className="px-3 py-2 text-right">Indent Value</th>
                  </tr>
                </thead>
                <tbody>
                  {view.items.map((it, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        {it.description}
                        {(it.grade || it.rm_type) && (
                          <div className="text-[10px] text-slate-600 mt-0.5">
                            {it.grade && <>Grade: <span className="font-semibold">{it.grade}</span></>}
                            {it.grade && it.rm_type && " · "}
                            {it.rm_type && <>Type: <span className="font-semibold">{it.rm_type}</span></>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.item_code || it.type_code || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                      <td className="px-3 py-2">{it.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{Number(it.approx_cost_per_unit ?? it.approx_cost ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{Number(it.indent_value ?? (it.quantity * (it.approx_cost_per_unit ?? it.approx_cost ?? 0))).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right text-sm space-y-0.5">
                <div>Indent Value: <span className="font-mono">₹{(view.indent_value ?? view.approx_value ?? 0).toLocaleString("en-IN")}</span></div>
                <div>Tax ({view.tax_rate || 0}%): <span className="font-mono">₹{(view.tax_amount || 0).toLocaleString("en-IN")}</span></div>
                <div className="font-semibold text-base">Total Indent Value: <span className="font-mono">₹{(view.total_indent_value ?? view.approx_value ?? 0).toLocaleString("en-IN")}</span></div>
                <div className="text-xs text-slate-500">L1: {view.level1_approved_by || "—"} · L2: {view.level2_approved_by || "—"}</div>
              </div>
              {(view.remarks || "").trim() && (
                <div className="mt-4 p-3 border-l-4 border-amber-400 bg-amber-50 rounded-r" data-testid="indent-view-remarks">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 mb-1">Remarks / Notes</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{view.remarks}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {view && (
              <button data-testid="dialog-edit-indent-btn" onClick={() => startEdit(view)} className="px-4 py-2 rounded-md border border-[#1D2951] text-[#1D2951] hover:bg-[#1D2951]/5 text-sm font-semibold inline-flex items-center gap-2 mr-auto">
                <Edit size={14} /> Edit{(view.status === "approved" || view.status === "rejected") ? " (CEO pwd)" : ""}
              </button>
            )}
            {view && canApprove(view) && (
              <div className="flex gap-2 justify-end">
                <button data-testid="reject-indent-btn" onClick={() => askPwd("reject")} className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm inline-flex items-center gap-2">
                  <X size={14} /> Reject
                </button>
                <button data-testid="approve-indent-btn" onClick={() => askPwd("approve")} className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm inline-flex items-center gap-2">
                  <Check size={14} /> Approve {view.status === "pending" && view.required_level === 2 ? "(Level 1)" : view.status === "pending_l2" ? "(Level 2)" : ""}
                </button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password confirm */}
      <Dialog open={!!pwdMode} onOpenChange={(o) => !o && setPwdMode(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm with password</DialogTitle>
            <DialogDescription>
              Enter the approver password to {pwdMode === "approve" ? "approve" : "reject"} this indent.
              {pwdMode === "approve" && <> Entering the <b>L2 password</b> here short-circuits both levels at once.</>}
            </DialogDescription>
          </DialogHeader>
          <input
            type="password"
            autoFocus
            data-testid="approval-pwd-input"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitDecision()}
            className={inputCls}
            placeholder="Your password"
          />
          <DialogFooter>
            <button onClick={() => setPwdMode(null)} className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
            <button onClick={submitDecision} data-testid="approval-pwd-submit" className="px-4 py-2 rounded-md bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk approval password dialog */}
      <Dialog open={bulkDialog} onOpenChange={(o) => !o && setBulkDialog(false)}>
        <DialogContent className="max-w-sm" data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {selectedIds.length} indent{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>
              Enter the Level-1 password to approve as L1. <b>Or enter the Level-2 password</b> to short-circuit
              both levels at once (only Admins can do this for &gt;₹1000 indents).
            </DialogDescription>
          </DialogHeader>
          <input
            type="password"
            autoFocus
            data-testid="bulk-pwd-input"
            value={bulkPwd}
            onChange={(e) => setBulkPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !bulkSubmitting && submitBulkApproval()}
            className={inputCls}
            placeholder="L1 or L2 password"
          />
          <DialogFooter>
            <button
              onClick={() => setBulkDialog(false)}
              disabled={bulkSubmitting}
              className="px-4 py-2 rounded-md border border-slate-300 text-sm disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              onClick={submitBulkApproval}
              disabled={bulkSubmitting}
              data-testid="bulk-pwd-submit"
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold"
            >
              {bulkSubmitting ? "Approving…" : `Approve ${selectedIds.length}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const Field = ({ label, required, children }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
    </div>
    {children}
  </div>
);
const Label = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);
const KV = ({ k, v }) => (<div><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div className="text-sm text-slate-900 font-medium">{v || "—"}</div></div>);

function PendingBudgetPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/indents/pending-budget-approval");
      setRows(data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/indents/${id}/approve-budget`);
      toast.success("Budget approval granted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    } finally { setBusy(null); }
  };
  if (!loading && rows.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg overflow-hidden" data-testid="pending-budget-panel">
      <div className="px-4 py-2 bg-amber-100 border-b border-amber-300 text-sm font-semibold text-amber-900 flex items-center justify-between">
        <span>⚠ Pending Budget Approval ({rows.length})</span>
        <span className="text-[10px] font-normal">Owner approval required — cost centre &ge; 75% of FY budget</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-amber-50/60 text-amber-900 uppercase tracking-wider">
              <th className="px-3 py-2 text-left">Indent #</th>
              <th className="px-3 py-2 text-left">Indenter</th>
              <th className="px-3 py-2 text-left">Cost Center</th>
              <th className="px-3 py-2 text-right">Indent Value</th>
              <th className="px-3 py-2 text-right">% Beyond 75</th>
              <th className="px-3 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-amber-200/70">
                <td className="px-3 py-2 font-mono font-semibold text-slate-900">{r.indent_no}</td>
                <td className="px-3 py-2">{r.intender_name}</td>
                <td className="px-3 py-2">{r.cost_center}</td>
                <td className="px-3 py-2 text-right font-mono">₹{Number(r.indent_value || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-rose-700">+{r.pct_beyond_75}%</td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => approve(r.id)}
                    disabled={busy === r.id}
                    data-testid={`approve-budget-${r.indent_no}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold px-3 py-1 rounded-md disabled:opacity-60"
                  >
                    {busy === r.id ? "Approving…" : "Owner Approve"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

